import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft,
  User,
  Calendar,
  DollarSign,
  CreditCard,
  Banknote,
  Building2,
  FileText,
  Package,
  AlertTriangle,
  Check,
  X,
  Plus,
  Printer,
  Ban,
  MessageCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SaleDetailPage = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newPayment, setNewPayment] = useState({ metodo_pago: 'EFECTIVO', monto: 0, referencia: '', banco: '' });
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Void modal
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidLoading, setVoidLoading] = useState(false);

  const fetchSale = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sales/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSale(response.data);
    } catch (error) {
      setError('Error al cargar la venta');
      console.error('Error fetching sale:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchSale();
  }, [fetchSale]);

  const formatCurrency = (amount) => {
    return `Q ${amount?.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const getStatusBadge = (estado, large = false) => {
    const styles = {
      PAGADA: 'bg-green-100 text-green-700 border-green-200',
      PARCIAL: 'bg-amber-100 text-amber-700 border-amber-200',
      PENDIENTE: 'bg-red-100 text-red-700 border-red-200',
      ANULADA: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    const labels = {
      PAGADA: 'Pagada',
      PARCIAL: 'Parcial',
      PENDIENTE: 'Pendiente',
      ANULADA: 'Anulada'
    };
    return (
      <Badge className={`${styles[estado] || 'bg-gray-100'} border ${large ? 'text-base px-4 py-1' : ''}`}>
        {labels[estado] || estado}
      </Badge>
    );
  };

  const addPayment = async () => {
    if (newPayment.monto <= 0) return;
    
    setPaymentLoading(true);
    try {
      await axios.post(`${API}/sales/${id}/pagos`, {
        pagos: [newPayment]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowPaymentModal(false);
      setNewPayment({ metodo_pago: 'EFECTIVO', monto: 0, referencia: '', banco: '' });
      fetchSale();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setPaymentLoading(false);
    }
  };

  const voidSale = async () => {
    if (!voidReason.trim()) {
      setError('El motivo de anulación es obligatorio');
      return;
    }
    
    setVoidLoading(true);
    try {
      await axios.post(`${API}/sales/${id}/anular`, {
        motivo_anulacion: voidReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowVoidModal(false);
      fetchSale();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al anular la venta');
    } finally {
      setVoidLoading(false);
    }
  };

  const canVoid = user?.role === 'admin' || user?.role === 'superadmin';
  const paymentMethodLabels = {
    EFECTIVO: 'Efectivo',
    TARJETA: 'Tarjeta',
    TRANSFERENCIA: 'Transferencia',
    CHEQUE: 'Cheque',
    OTRO: 'Otro'
  };

  // Print receipt in new window
  const handlePrintReceipt = () => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) return;

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Recibo - ${sale.numero_venta}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Courier New', monospace; 
            font-size: 12px; 
            padding: 10mm; 
            max-width: 80mm;
            line-height: 1.4;
          }
          .header { text-align: center; margin-bottom: 15px; border-bottom: 1px dashed #000; padding-bottom: 10px; }
          .clinic-name { font-size: 14px; font-weight: bold; }
          .title { font-size: 16px; font-weight: bold; margin: 10px 0; }
          .divider { border-top: 1px dashed #000; margin: 10px 0; }
          .row { display: flex; justify-content: space-between; margin: 3px 0; }
          .item { margin: 8px 0; }
          .item-name { font-weight: bold; }
          .item-detail { font-size: 11px; color: #555; margin-left: 10px; }
          .total-section { margin-top: 15px; border-top: 2px solid #000; padding-top: 10px; }
          .total { font-size: 16px; font-weight: bold; }
          .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; }
          .status { padding: 3px 8px; border-radius: 3px; font-size: 11px; display: inline-block; }
          .status-pagada { background: #d1fae5; color: #065f46; }
          .status-pendiente { background: #fee2e2; color: #991b1b; }
          .status-parcial { background: #fef3c7; color: #92400e; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="clinic-name">La Princesa - Clínicas y Ópticas</div>
          <div>Dr. Cristian R. Acevedo</div>
          <div>Médico Oftalmólogo</div>
        </div>
        
        <div class="title">RECIBO DE VENTA</div>
        
        <div class="row"><span>No.:</span><span>${sale.numero_venta}</span></div>
        <div class="row"><span>Fecha:</span><span>${sale.fecha_venta}</span></div>
        <div class="row"><span>Paciente:</span><span>${sale.nombre_paciente}</span></div>
        ${sale.profesional_nombre ? `<div class="row"><span>Doctor:</span><span>${sale.profesional_nombre}</span></div>` : ''}
        
        <div class="divider"></div>
        
        ${sale.lineas?.map(linea => `
          <div class="item">
            <div class="item-name">${linea.nombre}</div>
            <div class="row">
              <span class="item-detail">${linea.cantidad} x Q ${linea.precio_unitario?.toFixed(2)}</span>
              <span>Q ${linea.subtotal?.toFixed(2)}</span>
            </div>
          </div>
        `).join('') || ''}
        
        <div class="total-section">
          ${sale.descuento_total > 0 ? `
            <div class="row"><span>Subtotal:</span><span>Q ${(sale.total_venta + sale.descuento_total)?.toFixed(2)}</span></div>
            <div class="row"><span>Descuento:</span><span>-Q ${sale.descuento_total?.toFixed(2)}</span></div>
          ` : ''}
          <div class="row total"><span>TOTAL:</span><span>Q ${sale.total_venta?.toFixed(2)}</span></div>
          <div class="row"><span>Pagado:</span><span>Q ${sale.total_pagado?.toFixed(2)}</span></div>
          ${sale.saldo_pendiente > 0 ? `<div class="row" style="color: #991b1b;"><span>Saldo:</span><span>Q ${sale.saldo_pendiente?.toFixed(2)}</span></div>` : ''}
        </div>
        
        <div class="divider"></div>
        
        <div style="text-align: center; margin: 10px 0;">
          <span class="status status-${sale.estado_pago?.toLowerCase()}">${sale.estado_pago}</span>
        </div>
        
        <div class="footer">
          <p>¡Gracias por su preferencia!</p>
          <p>Atendido por: ${sale.atendido_por_nombre || 'N/A'}</p>
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  // Send receipt via WhatsApp
  const handleSendWhatsApp = () => {
    // Build message
    let message = `*RECIBO DE VENTA*\n`;
    message += `*La Princesa - Clínicas y Ópticas*\n`;
    message += `Dr. Cristian R. Acevedo\n`;
    message += `${'─'.repeat(20)}\n\n`;
    
    message += `📝 *No.:* ${sale.numero_venta}\n`;
    message += `📅 *Fecha:* ${sale.fecha_venta}\n`;
    message += `👤 *Paciente:* ${sale.nombre_paciente}\n`;
    if (sale.profesional_nombre) {
      message += `👨‍⚕️ *Doctor:* ${sale.profesional_nombre}\n`;
    }
    message += `\n${'─'.repeat(20)}\n\n`;
    
    message += `*DETALLE:*\n`;
    sale.lineas?.forEach((linea, idx) => {
      message += `${idx + 1}. ${linea.nombre}\n`;
      message += `   ${linea.cantidad} x Q${linea.precio_unitario?.toFixed(2)} = Q${linea.subtotal?.toFixed(2)}\n`;
    });
    
    message += `\n${'─'.repeat(20)}\n`;
    if (sale.descuento_total > 0) {
      message += `Subtotal: Q${(sale.total_venta + sale.descuento_total)?.toFixed(2)}\n`;
      message += `Descuento: -Q${sale.descuento_total?.toFixed(2)}\n`;
    }
    message += `*TOTAL: Q${sale.total_venta?.toFixed(2)}*\n`;
    message += `Pagado: Q${sale.total_pagado?.toFixed(2)}\n`;
    if (sale.saldo_pendiente > 0) {
      message += `⚠️ *Saldo pendiente: Q${sale.saldo_pendiente?.toFixed(2)}*\n`;
    }
    message += `\nEstado: ${sale.estado_pago}\n`;
    message += `\n_Gracias por su preferencia_ 🙏`;

    // Open WhatsApp - without phone number, user will select contact
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <p className="text-slate-600">Venta no encontrada</p>
        <Link to="/admin/ventas">
          <Button className="mt-4" variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Ventas
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sale-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/ventas">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]" style={{ fontFamily: 'Fraunces, serif' }}>
                {sale.numero_venta}
              </h1>
              {getStatusBadge(sale.estado_pago, true)}
            </div>
            <p className="text-slate-500 mt-1">
              {sale.fecha_venta} · Atendido por {sale.atendido_por_nombre || 'N/A'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {sale.estado_pago !== 'ANULADA' && (
            <>
              <Button variant="outline" onClick={handlePrintReceipt}>
                <Printer className="h-4 w-4 mr-2" />
                Imprimir
              </Button>
              <Button variant="outline" onClick={handleSendWhatsApp} className="text-green-600 hover:text-green-700 hover:bg-green-50">
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
              {canVoid && (
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setShowVoidModal(true)}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Anular
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          {error}
          <Button variant="ghost" size="sm" onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Void info */}
      {sale.estado_pago === 'ANULADA' && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Ban className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Venta Anulada</p>
                <p className="text-sm text-red-600">
                  Por {sale.anulada_por_nombre} el {sale.fecha_anulacion?.split('T')[0]}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  Motivo: {sale.motivo_anulacion}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
                <User className="h-5 w-5" />
                Paciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium text-slate-800 text-lg">{sale.nombre_paciente}</p>
              {sale.profesional_nombre && (
                <p className="text-sm text-slate-500 mt-1">
                  Profesional: {sale.profesional_nombre}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Lines */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Detalle de Ítems
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 text-xs font-medium text-slate-500 uppercase">Tipo</th>
                      <th className="text-left py-2 text-xs font-medium text-slate-500 uppercase">Descripción</th>
                      <th className="text-center py-2 text-xs font-medium text-slate-500 uppercase">Cant.</th>
                      <th className="text-right py-2 text-xs font-medium text-slate-500 uppercase">P. Unit.</th>
                      <th className="text-right py-2 text-xs font-medium text-slate-500 uppercase">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sale.lineas?.map((linea) => (
                      <tr key={linea.id} className="border-b border-slate-50">
                        <td className="py-3">
                          <Badge variant="outline" className="text-xs">
                            {linea.tipo_item === 'SERVICIO' ? (
                              <><FileText className="h-3 w-3 mr-1" /> Servicio</>
                            ) : (
                              <><Package className="h-3 w-3 mr-1" /> Producto</>
                            )}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <span className="font-medium text-slate-800">{linea.descripcion}</span>
                          {linea.nivel_precio && (
                            <span className="text-xs text-slate-400 ml-2">(Precio {linea.nivel_precio})</span>
                          )}
                        </td>
                        <td className="py-3 text-center font-mono">{linea.cantidad}</td>
                        <td className="py-3 text-right font-mono">{formatCurrency(linea.precio_unitario)}</td>
                        <td className="py-3 text-right font-mono font-semibold">{formatCurrency(linea.subtotal_linea)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Payments */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Pagos Registrados
                </CardTitle>
                {(sale.estado_pago === 'PENDIENTE' || sale.estado_pago === 'PARCIAL') && (
                  <Button
                    size="sm"
                    className="bg-[#0891B2]"
                    onClick={() => {
                      setNewPayment({ ...newPayment, monto: sale.saldo_pendiente });
                      setShowPaymentModal(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Registrar Pago
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {sale.pagos?.length === 0 ? (
                <div className="text-center py-6 text-slate-400">
                  <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Sin pagos registrados</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sale.pagos?.map((pago) => (
                    <div key={pago.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {pago.metodo_pago === 'EFECTIVO' && <Banknote className="h-5 w-5 text-green-600" />}
                        {pago.metodo_pago === 'TARJETA' && <CreditCard className="h-5 w-5 text-green-600" />}
                        {pago.metodo_pago === 'TRANSFERENCIA' && <Building2 className="h-5 w-5 text-green-600" />}
                        {!['EFECTIVO', 'TARJETA', 'TRANSFERENCIA'].includes(pago.metodo_pago) && <DollarSign className="h-5 w-5 text-green-600" />}
                        <div>
                          <p className="font-medium text-green-800">{paymentMethodLabels[pago.metodo_pago]}</p>
                          <p className="text-xs text-green-600">
                            {pago.fecha_pago} · {pago.registrado_por_nombre}
                            {pago.referencia && ` · Ref: ${pago.referencia}`}
                          </p>
                        </div>
                      </div>
                      <span className="font-mono font-bold text-green-800 text-lg">
                        {formatCurrency(pago.monto)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Totals */}
        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Resumen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-mono">{formatCurrency(sale.subtotal)}</span>
              </div>
              {sale.descuento_monto > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Descuento {sale.descuento_porcentaje > 0 ? `(${sale.descuento_porcentaje}%)` : ''}</span>
                  <span className="font-mono">-{formatCurrency(sale.descuento_monto)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-3">
                <span className="text-[#0B3B71]">Total</span>
                <span className="font-mono text-[#0B3B71]">{formatCurrency(sale.total)}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Pagado</span>
                <span className="font-mono">{formatCurrency(sale.total_pagado)}</span>
              </div>
              {sale.saldo_pendiente > 0 && (
                <div className="flex justify-between text-red-600 font-semibold">
                  <span>Saldo Pendiente</span>
                  <span className="font-mono">{formatCurrency(sale.saldo_pendiente)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {sale.notas && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Notas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700">{sale.notas}</p>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          {(sale.estado_pago === 'PENDIENTE' || sale.estado_pago === 'PARCIAL') && (
            <Button
              className="w-full bg-green-600 hover:bg-green-700 h-12"
              onClick={() => {
                setNewPayment({ ...newPayment, monto: sale.saldo_pendiente });
                setShowPaymentModal(true);
              }}
            >
              <DollarSign className="h-5 w-5 mr-2" />
              Registrar Pago ({formatCurrency(sale.saldo_pendiente)})
            </Button>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Método</Label>
              <Select value={newPayment.metodo_pago} onValueChange={(v) => setNewPayment({ ...newPayment, metodo_pago: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                  <SelectItem value="TARJETA">Tarjeta</SelectItem>
                  <SelectItem value="TRANSFERENCIA">Transferencia</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                  <SelectItem value="OTRO">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monto (Q)</Label>
              <Input
                type="number"
                value={newPayment.monto}
                onChange={(e) => setNewPayment({ ...newPayment, monto: parseFloat(e.target.value) || 0 })}
                className="mt-1 font-mono"
              />
            </div>
            {newPayment.metodo_pago !== 'EFECTIVO' && (
              <div>
                <Label>Referencia</Label>
                <Input
                  value={newPayment.referencia}
                  onChange={(e) => setNewPayment({ ...newPayment, referencia: e.target.value })}
                  className="mt-1"
                />
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPaymentModal(false)}>
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-[#0891B2]" 
                onClick={addPayment}
                disabled={paymentLoading}
              >
                {paymentLoading ? 'Guardando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Void Modal */}
      <Dialog open={showVoidModal} onOpenChange={setShowVoidModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Anular Venta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Esta acción no se puede deshacer. Si la venta incluía productos, se reingresarán al inventario.
            </p>
            <div>
              <Label>Motivo de anulación *</Label>
              <Input
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Describe el motivo..."
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowVoidModal(false)}>
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700" 
                onClick={voidSale}
                disabled={voidLoading || !voidReason.trim()}
              >
                {voidLoading ? 'Anulando...' : 'Confirmar Anulación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SaleDetailPage;
